// ── Research tree with multi-level upgrades ───────────────────────────────────
//
// Resource ID mapping (old → new):
//   wire        → copper
//   plating     → hull_debris
//   glass       → glass_shard
//   metal       → metal_alloy
//   crystal     → ionized_lens (combat/laser) or optical_processor (mining/optics)
//   composite   → synth_alloy (T1) or reinforced_frame (T2 gate)
//   energy      → fuel (early) or enriched_cells (T2 gate)
//   optics      → optical_processor
//   alloy       → titanium_plates (T2) or warp_coolant / kinetic_shielding (higher)
//   nano        → nano_glass (T2) or neural_link (T2/3 components)
//   plasma_cell → enriched_cells (T2) or star_fire / void_matter (T3)
//   singularity → quantum_core
//   darksteel   → self_healing_hull

function buildLevels(count, { getCost, getEffect, getLabel, isBig }) {
  return Array.from({ length: count }, (_, i) => {
    const lvl = i + 1;
    return {
      cost:   getCost(lvl),
      effect: getEffect(lvl),
      label:  getLabel ? getLabel(lvl) : undefined,
      isBig:  isBig ? isBig(lvl) : false,
    };
  });
}

const BIG_AT = new Set([1, 5, 10, 15, 20]);
const isBig  = lvl => BIG_AT.has(lvl);

// Exponential cost scaling — grows faster than old version
// base: starting quantity at lvl 1
// rate: per-level multiplier (e.g. 0.45 = 45% more each level)
function sc(base, rate, lvl) {
  return Math.round(base * Math.pow(1 + rate, lvl - 1));
}

// ══════════════════════════════════════════════════════════════════════════════
export const RESEARCH_TREE = [

  // ──────────────────────────────────────────────────────────────────────────
  // REFINERY — 5 levels, unlock-gated
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "refinery",
    name: "Refinery",
    desc: "Upgrade the ship's micro-foundry to unlock higher-tier forge recipes.",
    tier: 0, req: [],
    badge: "FORGE UPGRADE",
    levels: [
      // Lv1 — raw cost (tutorial gate)
      { cost: [{ id: "copper", qty: 20 }, { id: "hull_debris", qty: 14 }], costIsRaw: true,
        effect: { unlock: "refinery1" }, label: "Unlocks Smelter — Fuel, Metal Alloy & Synth Alloy recipes", isBig: true },
      // Lv2 — Forge Level 5: T1 Components
      { cost: [{ id: "metal_alloy", qty: 30 }, { id: "ionized_lens", qty: 18 }],
        effect: { unlock: "refinery2" }, label: "Unlocks Thermal Core, Ionized Lens, Reinforced Frame & more", isBig: true },
      // Lv3 — T2 Components
      { cost: [{ id: "metal_alloy", qty: 45 }, { id: "reinforced_frame", qty: 22 }, { id: "fuel", qty: 14 }],
        effect: { unlock: "refinery3" }, label: "Unlocks Warp Coolant, Kinetic Shielding & Neural Link recipes", isBig: true },
      // Lv4 — T3 Components
      { cost: [{ id: "enriched_cells", qty: 60 }, { id: "optical_processor", qty: 35 }, { id: "ionized_lens", qty: 40 }, { id: "reinforced_frame", qty: 40 }],
        effect: { unlock: "refinery4" }, label: "Unlocks Dimensional Tank, Self-Healing Hull recipes", isBig: true },
      // Lv5 — Endgame
      { cost: [{ id: "void_matter", qty: 20 }, { id: "neural_link", qty: 18 }, { id: "enriched_cells", qty: 12 }],
        effect: { unlock: "refinery5" }, label: "Unlocks Quantum Core recipe", isBig: true },
    ],
  },

  {
    id: "nav1",
    name: "Navigation Array",
    desc: "Restore the ship's navigation array to chart a course through the debris field.",
    tier: 1, req: [{ id: "dmg1", minLevel: 1 }],
    badge: "SYSTEM UNLOCK",
    levels: [
      { cost: [{ id: "synth_alloy", qty: 18 }, { id: "optical_processor", qty: 10 }, { id: "metal_alloy", qty: 20 }],
        effect: { unlock: "starmap" }, label: "Unlocks Starmap", isBig: true },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // SMELT QUEUE — 10 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "queue",
    name: "Forge Queue",
    desc: "Expand the smelter queue to process more jobs simultaneously.",
    tier: 0, req: [{ id: "refinery", minLevel: 1 }],
    badge: "QUEUE UPGRADE",
    levels: [
      { cost: [{ id: "copper", qty: 30 }, { id: "hull_debris", qty: 20 }], costIsRaw: true,
        effect: { stat: "smeltQueueMax", set: 3 }, label: "Queue → 3 slots", isBig: true },
      { cost: [{ id: "metal_alloy", qty: 18 }, { id: "synth_alloy", qty: 12 }],
        effect: { stat: "smeltQueueMax", set: 4 }, label: "Queue → 4 slots", isBig: false },
      { cost: [{ id: "metal_alloy", qty: 30 }, { id: "synth_alloy", qty: 20 }],
        effect: { stat: "smeltQueueMax", set: 5 }, label: "Queue → 5 slots", isBig: false },
      { cost: [{ id: "metal_alloy", qty: 45 }, { id: "synth_alloy", qty: 30 }, { id: "fuel", qty: 16 }],
        effect: { stat: "smeltQueueMax", set: 6 }, label: "Queue → 6 slots", isBig: false },
      { cost: [{ id: "fuel", qty: 50 }, { id: "metal_alloy", qty: 70 }, { id: "synth_alloy", qty: 50 }],
        effect: { stat: "smeltQueueMax", set: 10 }, label: "Queue → 10 slots", isBig: true },
      { cost: [{ id: "fuel", qty: 65 }, { id: "ionized_lens", qty: 28 }],
        effect: { stat: "smeltQueueMax", set: 12 }, label: "Queue → 12 slots", isBig: false },
      { cost: [{ id: "fuel", qty: 80 }, { id: "ionized_lens", qty: 40 }],
        effect: { stat: "smeltQueueMax", set: 14 }, label: "Queue → 14 slots", isBig: false },
      { cost: [{ id: "fuel", qty: 95 }, { id: "ionized_lens", qty: 55 }, { id: "optical_processor", qty: 28 }],
        effect: { stat: "smeltQueueMax", set: 16 }, label: "Queue → 16 slots", isBig: false },
      { cost: [{ id: "warp_coolant", qty: 12 }, { id: "fuel", qty: 110 }, { id: "ionized_lens", qty: 70 }],
        effect: { stat: "smeltQueueMax", set: 18 }, label: "Queue → 18 slots", isBig: false },
      { cost: [{ id: "warp_coolant", qty: 28 }, { id: "neural_link", qty: 18 }, { id: "enriched_cells", qty: 12 }],
        effect: { stat: "smeltQueueMax", set: 24 }, label: "Queue → 24 slots", isBig: true },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // BATCH FORGE — 3 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "batch1",
    name: "Batch Forge",
    desc: "Stack multiple runs of a recipe into a single forge slot.",
    tier: 1, req: [{ id: "refinery", minLevel: 1 }],
    badge: "FORGE UPGRADE",
    levels: [
      { cost: [{ id: "metal_alloy", qty: 40 }, { id: "synth_alloy", qty: 22 }],
        effect: { unlock: "batch_10" }, label: "Unlocks 10× batch size", isBig: true },
      { cost: [{ id: "fuel", qty: 55 }, { id: "synth_alloy", qty: 50 }, { id: "ionized_lens", qty: 36 }],
        effect: { unlock: "batch_500" }, label: "Unlocks 500× batch size", isBig: true },
      { cost: [{ id: "warp_coolant", qty: 22 }, { id: "neural_link", qty: 14 }, { id: "fuel", qty: 80 }],
        effect: { unlock: "batch_max" }, label: "Unlocks MAX batch size", isBig: true },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // LASER DAMAGE — 20 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "dmg1",
    name: "Laser Focus",
    desc: "Boost laser damage. Major upgrades shift the beam color and unlock new properties.",
    tier: 1, req: [],
    levels: buildLevels(20, {
      isBig,
      getEffect: lvl => ({ stat: "laserDamage", mult: isBig(lvl) ? 0.30 : 0.05 }),
      getLabel:  lvl => isBig(lvl)
        ? ["Red Beam (+30%)", "Orange Beam (+30%)", "Blue Beam (+30%)", "White Beam (+30%)", "Plasma Pulse (+30%)"][Math.floor((lvl - 1) / 5)]
        : `+5% laser damage`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "metal_alloy",  qty: sc(8, 0.5, lvl) }];
        if (lvl <= 5)  return [{ id: "metal_alloy",  qty: 55 }, { id: "ionized_lens", qty: 28 }];
        if (lvl <= 9)  return [{ id: "metal_alloy",  qty: sc(22, 0.4, lvl - 5) }, { id: "ionized_lens", qty: sc(12, 0.4, lvl - 5) }];
        if (lvl <= 10) return [{ id: "metal_alloy",  qty: 140 }, { id: "ionized_lens", qty: 75 }, { id: "fuel", qty: 40 }];
        if (lvl <= 14) return [{ id: "ionized_lens", qty: sc(30, 0.45, lvl - 10) }, { id: "fuel", qty: sc(18, 0.45, lvl - 10) }, { id: "synth_alloy", qty: sc(20, 0.4, lvl - 10) }];
        if (lvl <= 15) return [{ id: "fuel",  qty: 220 }, { id: "synth_alloy", qty: 140 }, { id: "kinetic_shielding", qty: 18 }];
        if (lvl <= 19) return [{ id: "fuel",  qty: sc(55, 0.45, lvl - 15) }, { id: "kinetic_shielding", qty: sc(8, 0.5, lvl - 15) }, { id: "neural_link", qty: sc(4, 0.5, lvl - 15) }];
        return [{ id: "kinetic_shielding", qty: 90 }, { id: "neural_link", qty: 50 }, { id: "enriched_cells", qty: 28 }];
      },
    }),
  },

  // ──────────────────────────────────────────────────────────────────────────
  // LASER RANGE — 20 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "rng1",
    name: "Focal Extension",
    desc: "Extend the laser beam's reach.",
    tier: 1, req: [],
    levels: buildLevels(20, {
      isBig,
      getEffect: lvl => ({ stat: "laserRange", add: isBig(lvl) ? 50 : 10 }),
      getLabel:  lvl => isBig(lvl) ? `+50px range` : `+10px range`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "optical_processor", qty: sc(6, 0.45, lvl) }];
        if (lvl <= 5)  return [{ id: "optical_processor", qty: 48 }, { id: "metal_alloy", qty: 22 }];
        if (lvl <= 9)  return [{ id: "optical_processor", qty: sc(18, 0.4, lvl - 5) }, { id: "metal_alloy", qty: sc(10, 0.4, lvl - 5) }];
        if (lvl <= 10) return [{ id: "optical_processor", qty: 110 }, { id: "metal_alloy", qty: 60 }, { id: "fuel", qty: 30 }];
        if (lvl <= 14) return [{ id: "optical_processor", qty: sc(28, 0.42, lvl - 10) }, { id: "fuel", qty: sc(16, 0.42, lvl - 10) }];
        if (lvl <= 15) return [{ id: "optical_processor", qty: 200 }, { id: "fuel", qty: 110 }, { id: "warp_coolant", qty: 14 }];
        if (lvl <= 19) return [{ id: "optical_processor", qty: sc(50, 0.4, lvl - 15) }, { id: "warp_coolant", qty: sc(6, 0.5, lvl - 15) }];
        return [{ id: "warp_coolant", qty: 70 }, { id: "neural_link", qty: 36 }, { id: "enriched_cells", qty: 18 }];
      },
    }),
  },

  // ──────────────────────────────────────────────────────────────────────────
  // ROTATION SPEED — 10 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "spd1",
    name: "Turret Bearings",
    desc: "Increase the laser turret's rotation speed for faster target acquisition.",
    tier: 1, req: [],
    levels: buildLevels(10, {
      isBig,
      getEffect: lvl => ({ stat: "rotSpeed", mult: isBig(lvl) ? 0.40 : 0.10 }),
      getLabel:  lvl => isBig(lvl) ? `+40% rotation speed` : `+10% rotation speed`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "synth_alloy", qty: sc(10, 0.5, lvl) }, { id: "metal_alloy", qty: sc(5, 0.5, lvl) }];
        if (lvl <= 5)  return [{ id: "synth_alloy", qty: 80 }, { id: "metal_alloy", qty: 40 }, { id: "fuel", qty: 20 }];
        if (lvl <= 9)  return [{ id: "synth_alloy", qty: sc(22, 0.45, lvl - 5) }, { id: "fuel", qty: sc(12, 0.45, lvl - 5) }];
        return [{ id: "synth_alloy", qty: 180 }, { id: "fuel", qty: 95 }, { id: "warp_coolant", qty: 16 }];
      },
    }),
  },

  // ──────────────────────────────────────────────────────────────────────────
  // BONUS LOOT — 15 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "loot1",
    name: "Salvage Scanner",
    desc: "Increase the number of items extracted from each debris piece.",
    tier: 1, req: [],
    levels: buildLevels(15, {
      isBig,
      getEffect: lvl => ({ stat: "bonusLoot", add: isBig(lvl) ? 2 : 1 }),
      getLabel:  lvl => isBig(lvl) ? `+2 items per debris` : `+1 item per debris`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "ionized_lens", qty: sc(8, 0.45, lvl) }];
        if (lvl <= 5)  return [{ id: "ionized_lens", qty: 55 }, { id: "optical_processor", qty: 20 }];
        if (lvl <= 9)  return [{ id: "ionized_lens", qty: sc(18, 0.4, lvl - 5) }, { id: "optical_processor", qty: sc(8, 0.4, lvl - 5) }];
        if (lvl <= 10) return [{ id: "ionized_lens", qty: 110 }, { id: "optical_processor", qty: 48 }, { id: "fuel", qty: 28 }];
        if (lvl <= 14) return [{ id: "ionized_lens", qty: sc(28, 0.42, lvl - 10) }, { id: "fuel", qty: sc(16, 0.42, lvl - 10) }, { id: "synth_alloy", qty: sc(12, 0.42, lvl - 10) }];
        return [{ id: "fuel", qty: 200 }, { id: "warp_coolant", qty: 22 }, { id: "neural_link", qty: 12 }];
      },
    }),
  },

  // ──────────────────────────────────────────────────────────────────────────
  // BEAM WIDTH — 15 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "width1",
    name: "Wide Beam",
    desc: "Increase beam width so it's easier to hit spinning or fast-moving debris.",
    tier: 2, req: [{ id: "rng1", minLevel: 1 }],
    levels: buildLevels(15, {
      isBig,
      getEffect: lvl => ({ stat: "beamWidth", mult: isBig(lvl) ? 0.50 : 0.10 }),
      getLabel:  lvl => isBig(lvl) ? `+50% beam width` : `+10% beam width`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "optical_processor", qty: sc(12, 0.5, lvl) }, { id: "fuel", qty: sc(5, 0.5, lvl) }];
        if (lvl <= 5)  return [{ id: "optical_processor", qty: 90 }, { id: "fuel", qty: 38 }, { id: "synth_alloy", qty: 25 }];
        if (lvl <= 9)  return [{ id: "optical_processor", qty: sc(24, 0.42, lvl - 5) }, { id: "fuel", qty: sc(14, 0.42, lvl - 5) }, { id: "synth_alloy", qty: sc(10, 0.42, lvl - 5) }];
        if (lvl <= 10) return [{ id: "optical_processor", qty: 160 }, { id: "fuel", qty: 90 }, { id: "warp_coolant", qty: 14 }];
        if (lvl <= 14) return [{ id: "optical_processor", qty: sc(40, 0.4, lvl - 10) }, { id: "warp_coolant", qty: sc(6, 0.5, lvl - 10) }, { id: "neural_link", qty: sc(3, 0.5, lvl - 10) }];
        return [{ id: "warp_coolant", qty: 65 }, { id: "neural_link", qty: 38 }, { id: "enriched_cells", qty: 20 }];
      },
    }),
  },

  // ──────────────────────────────────────────────────────────────────────────
  // ATTRACT RANGE — 20 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "attract1",
    name: "Tractor Beam",
    desc: "Extend the tractor beam pull radius for automatic wreck collection.",
    tier: 2, req: [{ id: "spd1", minLevel: 1 }],
    levels: buildLevels(20, {
      isBig,
      getEffect: lvl => ({ stat: "attractRange", add: isBig(lvl) ? 100 : 20 }),
      getLabel:  lvl => isBig(lvl) ? `+100px tractor range` : `+20px tractor range`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "fuel", qty: sc(10, 0.45, lvl) }, { id: "synth_alloy", qty: sc(6, 0.45, lvl) }];
        if (lvl <= 5)  return [{ id: "fuel", qty: 75 }, { id: "synth_alloy", qty: 45 }, { id: "ionized_lens", qty: 22 }];
        if (lvl <= 9)  return [{ id: "fuel", qty: sc(20, 0.4, lvl - 5) }, { id: "synth_alloy", qty: sc(12, 0.4, lvl - 5) }, { id: "ionized_lens", qty: sc(8, 0.4, lvl - 5) }];
        if (lvl <= 10) return [{ id: "fuel", qty: 130 }, { id: "ionized_lens", qty: 70 }, { id: "warp_coolant", qty: 16 }];
        if (lvl <= 14) return [{ id: "fuel", qty: sc(35, 0.42, lvl - 10) }, { id: "warp_coolant", qty: sc(7, 0.5, lvl - 10) }];
        if (lvl <= 15) return [{ id: "fuel", qty: 240 }, { id: "warp_coolant", qty: 55 }, { id: "neural_link", qty: 16 }];
        if (lvl <= 19) return [{ id: "warp_coolant", qty: sc(14, 0.45, lvl - 15) }, { id: "neural_link", qty: sc(6, 0.5, lvl - 15) }];
        return [{ id: "warp_coolant", qty: 95 }, { id: "neural_link", qty: 55 }, { id: "enriched_cells", qty: 30 }];
      },
    }),
  },

  // ──────────────────────────────────────────────────────────────────────────
  // SMELT SPEED — 20 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "smelt1",
    name: "Forge Drive",
    desc: "Accelerate smelting. Major upgrades introduce parallel processing and auto-routines.",
    tier: 2, req: [{ id: "dmg1", minLevel: 1 }],
    levels: buildLevels(20, {
      isBig,
      getEffect: lvl => ({ stat: "smeltSpeed", mult: isBig(lvl) ? 0.50 : 0.10 }),
      getLabel:  lvl => isBig(lvl) ? `+50% smelt speed` : `+10% smelt speed`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "fuel", qty: sc(10, 0.45, lvl) }, { id: "metal_alloy", qty: sc(10, 0.45, lvl) }];
        if (lvl <= 5)  return [{ id: "fuel", qty: 80 }, { id: "metal_alloy", qty: 80 }, { id: "synth_alloy", qty: 30 }];
        if (lvl <= 9)  return [{ id: "fuel", qty: sc(22, 0.4, lvl - 5) }, { id: "metal_alloy", qty: sc(22, 0.4, lvl - 5) }, { id: "synth_alloy", qty: sc(10, 0.4, lvl - 5) }];
        if (lvl <= 10) return [{ id: "fuel", qty: 150 }, { id: "synth_alloy", qty: 100 }, { id: "warp_coolant", qty: 18 }];
        if (lvl <= 14) return [{ id: "fuel", qty: sc(40, 0.42, lvl - 10) }, { id: "warp_coolant", qty: sc(8, 0.5, lvl - 10) }, { id: "synth_alloy", qty: sc(25, 0.4, lvl - 10) }];
        if (lvl <= 15) return [{ id: "fuel", qty: 270 }, { id: "warp_coolant", qty: 60 }, { id: "neural_link", qty: 18 }];
        if (lvl <= 19) return [{ id: "warp_coolant", qty: sc(16, 0.45, lvl - 15) }, { id: "neural_link", qty: sc(7, 0.5, lvl - 15) }];
        return [{ id: "warp_coolant", qty: 105 }, { id: "neural_link", qty: 60 }, { id: "enriched_cells", qty: 32 }];
      },
    }),
  },

  // ──────────────────────────────────────────────────────────────────────────
  // FORGE YIELD — 15 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "smeltYield1",
    name: "Forge Yield",
    desc: "Produce more materials from each smelt.",
    tier: 2, req: [{ id: "smelt1", minLevel: 1 }],
    levels: buildLevels(15, {
      isBig,
      getEffect: lvl => ({ stat: "smeltYield", mult: isBig(lvl) ? 0.30 : 0.08 }),
      getLabel:  lvl => isBig(lvl) ? `+30% smelt output` : `+8% smelt output`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "fuel", qty: sc(12, 0.45, lvl) }, { id: "ionized_lens", qty: sc(8, 0.45, lvl) }];
        if (lvl <= 5)  return [{ id: "fuel", qty: 95 }, { id: "ionized_lens", qty: 60 }, { id: "synth_alloy", qty: 30 }];
        if (lvl <= 9)  return [{ id: "fuel", qty: sc(25, 0.42, lvl - 5) }, { id: "ionized_lens", qty: sc(16, 0.42, lvl - 5) }, { id: "synth_alloy", qty: sc(10, 0.42, lvl - 5) }];
        if (lvl <= 10) return [{ id: "fuel", qty: 180 }, { id: "ionized_lens", qty: 110 }, { id: "warp_coolant", qty: 20 }];
        if (lvl <= 14) return [{ id: "fuel", qty: sc(45, 0.4, lvl - 10) }, { id: "warp_coolant", qty: sc(9, 0.5, lvl - 10) }, { id: "neural_link", qty: sc(4, 0.5, lvl - 10) }];
        return [{ id: "warp_coolant", qty: 80 }, { id: "neural_link", qty: 45 }, { id: "enriched_cells", qty: 24 }];
      },
    }),
  },

  // ──────────────────────────────────────────────────────────────────────────
  // FORGE EFFICIENCY — 10 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "smeltCost1",
    name: "Forge Efficiency",
    desc: "Consume fewer raw materials per smelt.",
    tier: 3, req: [{ id: "smeltYield1", minLevel: 3 }],
    levels: buildLevels(10, {
      isBig,
      getEffect: lvl => ({ stat: "smeltCostMult", add: isBig(lvl) ? -0.10 : -0.03 }),
      getLabel:  lvl => isBig(lvl) ? `-10% material cost` : `-3% material cost`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "fuel", qty: sc(30, 0.45, lvl) }, { id: "synth_alloy", qty: sc(18, 0.45, lvl) }, { id: "warp_coolant", qty: sc(5, 0.5, lvl) }];
        if (lvl <= 5)  return [{ id: "fuel", qty: 200 }, { id: "synth_alloy", qty: 120 }, { id: "warp_coolant", qty: 28 }];
        if (lvl <= 9)  return [{ id: "fuel", qty: sc(50, 0.4, lvl - 5) }, { id: "warp_coolant", qty: sc(12, 0.5, lvl - 5) }, { id: "neural_link", qty: sc(5, 0.5, lvl - 5) }];
        return [{ id: "warp_coolant", qty: 110 }, { id: "neural_link", qty: 65 }, { id: "enriched_cells", qty: 38 }];
      },
    }),
  },

  // ──────────────────────────────────────────────────────────────────────────
  // SPAWN RATE — 15 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "spawn1",
    name: "Debris Magnet",
    desc: "Increase debris spawn rate so more wreckage appears in the field.",
    tier: 2, req: [{ id: "loot1", minLevel: 1 }],
    levels: buildLevels(15, {
      isBig,
      getEffect: lvl => ({ stat: "spawnRate", mult: isBig(lvl) ? 0.60 : 0.15 }),
      getLabel:  lvl => isBig(lvl) ? `+60% spawn rate` : `+15% spawn rate`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "synth_alloy", qty: sc(12, 0.45, lvl) }, { id: "ionized_lens", qty: sc(8, 0.45, lvl) }];
        if (lvl <= 5)  return [{ id: "synth_alloy", qty: 90 }, { id: "ionized_lens", qty: 60 }, { id: "fuel", qty: 28 }];
        if (lvl <= 9)  return [{ id: "synth_alloy", qty: sc(24, 0.4, lvl - 5) }, { id: "ionized_lens", qty: sc(15, 0.4, lvl - 5) }, { id: "fuel", qty: sc(10, 0.4, lvl - 5) }];
        if (lvl <= 10) return [{ id: "synth_alloy", qty: 170 }, { id: "fuel", qty: 100 }, { id: "warp_coolant", qty: 20 }];
        if (lvl <= 14) return [{ id: "synth_alloy", qty: sc(42, 0.4, lvl - 10) }, { id: "warp_coolant", qty: sc(8, 0.5, lvl - 10) }, { id: "neural_link", qty: sc(4, 0.5, lvl - 10) }];
        return [{ id: "warp_coolant", qty: 75 }, { id: "neural_link", qty: 42 }, { id: "enriched_cells", qty: 22 }];
      },
    }),
  },

  // ──────────────────────────────────────────────────────────────────────────
  // DEBRIS CAP — 8 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "debrisCap1",
    name: "Field Density",
    desc: "Increase the maximum number of debris pieces that can appear on screen at once.",
    tier: 2, req: [{ id: "spawn1", minLevel: 1 }],
    levels: buildLevels(8, {
      isBig,
      getEffect: lvl => ({ stat: "debrisCap", add: isBig(lvl) ? 4 : 2 }),
      getLabel:  lvl => isBig(lvl) ? `+4 max debris` : `+2 max debris`,
      getCost: lvl => {
        if (lvl <= 3)  return [{ id: "synth_alloy", qty: sc(14, 0.45, lvl) }, { id: "metal_alloy", qty: sc(10, 0.45, lvl) }];
        if (lvl <= 5)  return [{ id: "synth_alloy", qty: sc(60, 0.4, lvl - 3) }, { id: "fuel", qty: sc(30, 0.4, lvl - 3) }];
        return         [{ id: "synth_alloy", qty: sc(140, 0.4, lvl - 5) }, { id: "warp_coolant", qty: sc(18, 0.5, lvl - 5) }];
      },
    }),
  },

  // ──────────────────────────────────────────────────────────────────────────
  // AUTOPILOT — 1 level
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "autopilot",
    name: "Autopilot",
    desc: "Ship AI navigates to debris automatically — hands-free harvesting.",
    tier: 2, req: [{ id: "spawn1", minLevel: 2 }],
    levels: [
      { cost: [{ id: "synth_alloy", qty: 35 }, { id: "fuel", qty: 20 }, { id: "optical_processor", qty: 12 }],
        effect: { flag: "autoNavUnlocked" }, label: "Unlocks Autopilot toggle", isBig: true },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // MINING DRONES — 5 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "drones",
    name: "Mining Drones",
    desc: "Deploy autonomous drones that independently target and harvest debris.",
    tier: 3, req: [{ id: "autopilot", minLevel: 1 }],
    levels: buildLevels(5, {
      isBig,
      getEffect: lvl => ({ stat: "droneMax", add: 1 }),
      getLabel:  lvl => `+1 drone slot (total ${lvl})`,
      getCost: lvl => {
        if (lvl <= 2)  return [{ id: "synth_alloy", qty: sc(50, 0.5, lvl) }, { id: "fuel", qty: sc(30, 0.5, lvl) }, { id: "optical_processor", qty: sc(18, 0.5, lvl) }];
        if (lvl <= 3)  return [{ id: "synth_alloy", qty: 180 }, { id: "warp_coolant", qty: 22 }, { id: "fuel", qty: 80 }];
        return         [{ id: "warp_coolant", qty: sc(30, 0.55, lvl - 3) }, { id: "neural_link", qty: sc(15, 0.55, lvl - 3) }, { id: "fuel", qty: sc(90, 0.4, lvl - 3) }];
      },
    }),
  },

  // ──────────────────────────────────────────────────────────────────────────
  // AUTO-BREAK — 10 levels
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "auto1",
    name: "Auto-Salvage",
    desc: "Debris passively decays over time even without the laser.",
    tier: 3, req: [{ id: "loot1", minLevel: 3 }, { id: "attract1", minLevel: 1 }],
    levels: buildLevels(10, {
      isBig,
      getEffect: lvl => ({ stat: "autoBreak", add: isBig(lvl) ? 0.15 : 0.03 }),
      getLabel:  lvl => isBig(lvl) ? `+15% auto-decay` : `+3% auto-decay`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "synth_alloy", qty: sc(22, 0.5, lvl) }, { id: "fuel", qty: sc(14, 0.5, lvl) }, { id: "optical_processor", qty: sc(8, 0.5, lvl) }];
        if (lvl <= 5)  return [{ id: "synth_alloy", qty: 160 }, { id: "fuel", qty: 100 }, { id: "warp_coolant", qty: 18 }];
        if (lvl <= 9)  return [{ id: "synth_alloy", qty: sc(40, 0.4, lvl - 5) }, { id: "warp_coolant", qty: sc(8, 0.5, lvl - 5) }, { id: "neural_link", qty: sc(4, 0.5, lvl - 5) }];
        return [{ id: "warp_coolant", qty: 120 }, { id: "neural_link", qty: 70 }, { id: "enriched_cells", qty: 38 }];
      },
    }),
  },

  // ──────────────────────────────────────────────────────────────────────────
  // COMBAT BRANCH
  // ──────────────────────────────────────────────────────────────────────────

  {
    id: "hull1",
    name: "Hull Plating",
    desc: "Reinforce the ship hull to withstand more punishment in combat.",
    tier: 2, req: [],
    levels: buildLevels(15, {
      isBig,
      getEffect: lvl => ({ stat: "playerMaxHp", add: isBig(lvl) ? 30 : 8 }),
      getLabel:  lvl => isBig(lvl) ? `+30 max HP` : `+8 max HP`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "metal_alloy", qty: sc(12, 0.45, lvl) }, { id: "synth_alloy", qty: sc(6, 0.45, lvl) }];
        if (lvl <= 5)  return [{ id: "metal_alloy", qty: 90 }, { id: "synth_alloy", qty: 45 }, { id: "warp_coolant", qty: 10 }];
        if (lvl <= 9)  return [{ id: "metal_alloy", qty: sc(25, 0.4, lvl - 5) }, { id: "synth_alloy", qty: sc(14, 0.4, lvl - 5) }, { id: "warp_coolant", qty: sc(4, 0.5, lvl - 5) }];
        if (lvl <= 10) return [{ id: "metal_alloy", qty: 170 }, { id: "warp_coolant", qty: 28 }, { id: "neural_link", qty: 10 }];
        if (lvl <= 14) return [{ id: "metal_alloy", qty: sc(42, 0.4, lvl - 10) }, { id: "warp_coolant", qty: sc(10, 0.5, lvl - 10) }, { id: "neural_link", qty: sc(4, 0.5, lvl - 10) }];
        return [{ id: "warp_coolant", qty: 90 }, { id: "neural_link", qty: 55 }, { id: "enriched_cells", qty: 28 }];
      },
    }),
  },

  {
    id: "shield1",
    name: "Shield Generator",
    desc: "Install a rechargeable energy shield that absorbs damage before hull integrity is lost.",
    tier: 3, req: [{ id: "hull1", minLevel: 3 }],
    badge: "SHIELD UNLOCK",
    levels: buildLevels(10, {
      isBig,
      getEffect: lvl => ({ stat: "shieldMax", add: isBig(lvl) ? 60 : 15 }),
      getLabel:  lvl => lvl === 1 ? `Shield unlocked (+60)` : isBig(lvl) ? `+60 max shield` : `+15 max shield`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "fuel", qty: sc(28, 0.5, lvl) }, { id: "ionized_lens", qty: sc(18, 0.5, lvl) }, { id: "synth_alloy", qty: sc(12, 0.5, lvl) }];
        if (lvl <= 5)  return [{ id: "fuel", qty: 210 }, { id: "ionized_lens", qty: 130 }, { id: "warp_coolant", qty: 22 }];
        if (lvl <= 9)  return [{ id: "fuel", qty: sc(55, 0.42, lvl - 5) }, { id: "warp_coolant", qty: sc(10, 0.5, lvl - 5) }, { id: "neural_link", qty: sc(5, 0.5, lvl - 5) }];
        return [{ id: "fuel", qty: 350 }, { id: "warp_coolant", qty: 80 }, { id: "neural_link", qty: 48 }, { id: "enriched_cells", qty: 24 }];
      },
    }),
  },

  {
    id: "engine1",
    name: "Engine Thruster",
    desc: "Upgrade ship thrusters for faster movement across the battlefield.",
    tier: 2, req: [],
    levels: buildLevels(10, {
      isBig,
      getEffect: lvl => ({ stat: "shipSpeed", mult: isBig(lvl) ? 0.30 : 0.08 }),
      getLabel:  lvl => isBig(lvl) ? `+30% ship speed` : `+8% ship speed`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "synth_alloy", qty: sc(14, 0.5, lvl) }, { id: "fuel", qty: sc(8, 0.5, lvl) }];
        if (lvl <= 5)  return [{ id: "synth_alloy", qty: 100 }, { id: "fuel", qty: 58 }, { id: "warp_coolant", qty: 12 }];
        if (lvl <= 9)  return [{ id: "synth_alloy", qty: sc(28, 0.42, lvl - 5) }, { id: "warp_coolant", qty: sc(6, 0.5, lvl - 5) }, { id: "neural_link", qty: sc(3, 0.5, lvl - 5) }];
        return [{ id: "synth_alloy", qty: 200 }, { id: "warp_coolant", qty: 55 }, { id: "neural_link", qty: 32 }];
      },
    }),
  },

  {
    id: "blaster1",
    name: "Blaster Cannon",
    desc: "Upgrade the ship's blaster cannon used to destroy enemy ships in combat.",
    tier: 2, req: [],
    levels: buildLevels(15, {
      isBig,
      getEffect: lvl => ({ stat: "blasterDamage", mult: isBig(lvl) ? 0.40 : 0.08 }),
      getLabel:  lvl => isBig(lvl) ? `+40% blaster damage` : `+8% blaster damage`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "metal_alloy", qty: sc(14, 0.5, lvl) }, { id: "fuel", qty: sc(7, 0.5, lvl) }];
        if (lvl <= 5)  return [{ id: "metal_alloy", qty: 105 }, { id: "fuel", qty: 52 }, { id: "warp_coolant", qty: 12 }];
        if (lvl <= 9)  return [{ id: "metal_alloy", qty: sc(28, 0.42, lvl - 5) }, { id: "warp_coolant", qty: sc(5, 0.5, lvl - 5) }, { id: "fuel", qty: sc(16, 0.42, lvl - 5) }];
        if (lvl <= 10) return [{ id: "metal_alloy", qty: 200 }, { id: "kinetic_shielding", qty: 35 }, { id: "enriched_cells", qty: 10 }];
        if (lvl <= 14) return [{ id: "kinetic_shielding", qty: sc(10, 0.5, lvl - 10) }, { id: "enriched_cells", qty: sc(4, 0.5, lvl - 10) }, { id: "fuel", qty: sc(40, 0.4, lvl - 10) }];
        return [{ id: "kinetic_shielding", qty: 100 }, { id: "enriched_cells", qty: 55 }, { id: "neural_link", qty: 40 }];
      },
    }),
  },

  {
    id: "firerate1",
    name: "Rapid Fire",
    desc: "Overclock the blaster's firing mechanism for a higher rate of fire.",
    tier: 3, req: [{ id: "blaster1", minLevel: 2 }],
    levels: buildLevels(10, {
      isBig,
      getEffect: lvl => ({ stat: "blasterFireRate", add: isBig(lvl) ? -8 : -2 }),
      getLabel:  lvl => isBig(lvl) ? `-8 frames between shots` : `-2 frames between shots`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "fuel", qty: sc(20, 0.5, lvl) }, { id: "synth_alloy", qty: sc(12, 0.5, lvl) }];
        if (lvl <= 5)  return [{ id: "fuel", qty: 150 }, { id: "synth_alloy", qty: 90 }, { id: "warp_coolant", qty: 16 }];
        if (lvl <= 9)  return [{ id: "fuel", qty: sc(40, 0.42, lvl - 5) }, { id: "warp_coolant", qty: sc(8, 0.5, lvl - 5) }, { id: "neural_link", qty: sc(4, 0.5, lvl - 5) }];
        return [{ id: "fuel", qty: 290 }, { id: "warp_coolant", qty: 75 }, { id: "neural_link", qty: 44 }];
      },
    }),
  },

  {
    id: "multishot1",
    name: "Spread Fire",
    desc: "Fire multiple blaster bolts per shot in a spread pattern.",
    tier: 4, req: [{ id: "blaster1", minLevel: 5 }, { id: "firerate1", minLevel: 3 }],
    badge: "MULTISHOT",
    levels: buildLevels(5, {
      isBig: () => true,
      getEffect: lvl => ({ stat: "blasterCount", set: lvl + 1 }),
      getLabel:  lvl => `${lvl + 1} bullets per shot`,
      getCost: lvl => [
        { id: "kinetic_shielding", qty: sc(28, 0.65, lvl) },
        { id: "enriched_cells",    qty: sc(16, 0.65, lvl) },
        { id: "fuel",              qty: sc(35, 0.55, lvl) },
      ],
    }),
  },

  // ══════════════════════════════════════════════════════════════════════════
  // STARMAP BRANCH
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: "fuel_efficiency",
    name: "Warp Condenser",
    desc: "Compress jump fuel expenditure. Fast-travelling to discovered nodes costs less fuel.",
    tier: 2, req: [{ id: "nav1", minLevel: 1 }],
    badge: "STARMAP",
    levels: [
      { cost: [{ id: "fuel", qty: 45 }, { id: "synth_alloy", qty: 30 }],
        effect: { unlock: "fast_travel_discount_1" },
        label: "Fast travel costs −2 fuel (4 total)", isBig: false },
      { cost: [{ id: "fuel", qty: 80 }, { id: "optical_processor", qty: 35 }, { id: "synth_alloy", qty: 50 }],
        effect: { unlock: "fast_travel_discount_2" },
        label: "Fast travel costs −2 fuel (2 total)", isBig: false },
      { cost: [{ id: "warp_coolant", qty: 18 }, { id: "fuel", qty: 120 }, { id: "ionized_lens", qty: 60 }],
        effect: { stat: "fastTravelFree", set: 0 },
        label: "Fast travel costs 0 fuel — Quantum routing", isBig: true },
      { cost: [{ id: "warp_coolant", qty: 35 }, { id: "neural_link", qty: 20 }, { id: "fuel", qty: 160 }],
        effect: { unlock: "fast_travel_free_boost" },
        label: "Sector jump cost reduced by 1", isBig: false },
      { cost: [{ id: "neural_link", qty: 45 }, { id: "enriched_cells", qty: 24 }, { id: "warp_coolant", qty: 55 }],
        effect: { stat: "fastTravelFree", set: 1 },
        label: "All fast travel is FREE — full warp matrix", isBig: true },
    ],
  },

  {
    id: "node_scanner",
    name: "Node Scanner",
    desc: "Spend a charge to reveal hidden adjacent nodes without travelling to them.",
    tier: 2, req: [{ id: "nav1", minLevel: 1 }],
    badge: "STARMAP",
    levels: [
      { cost: [{ id: "optical_processor", qty: 40 }, { id: "fuel", qty: 28 }, { id: "synth_alloy", qty: 22 }],
        effect: { stat: "scannerCharges", add: 1 }, extraEffect: { stat: "scannerReveal", add: 1 },
        label: "1 scanner charge per sector — reveals 1 adjacent node", isBig: true },
      { cost: [{ id: "optical_processor", qty: 65 }, { id: "fuel", qty: 55 }, { id: "ionized_lens", qty: 32 }],
        effect: { stat: "scannerCharges", add: 1 },
        label: "+1 charge — 2 charges total", isBig: false },
      { cost: [{ id: "warp_coolant", qty: 20 }, { id: "optical_processor", qty: 90 }, { id: "fuel", qty: 80 }],
        effect: { stat: "scannerReveal", add: 1 },
        label: "Each scan now reveals 2 adjacent nodes", isBig: true },
      { cost: [{ id: "warp_coolant", qty: 40 }, { id: "neural_link", qty: 24 }, { id: "optical_processor", qty: 120 }],
        effect: { stat: "scannerCharges", add: 1 }, extraEffect: { stat: "scannerReveal", add: 1 },
        label: "3 charges, 3 nodes revealed per scan", isBig: true },
    ],
  },

  {
    id: "sector_diversity",
    name: "Cartographic Matrix",
    desc: "Retune sector generation to favour material sites and fuel depots.",
    tier: 3, req: [{ id: "node_scanner", minLevel: 1 }, { id: "fuel_efficiency", minLevel: 1 }],
    badge: "STARMAP",
    levels: [
      { cost: [{ id: "synth_alloy", qty: 70 }, { id: "optical_processor", qty: 50 }, { id: "fuel", qty: 50 }],
        effect: { stat: "sectorDiversity", add: 1 },
        label: "+1 diversity — material sites more common", isBig: true },
      { cost: [{ id: "warp_coolant", qty: 30 }, { id: "ionized_lens", qty: 70 }, { id: "fuel", qty: 90 }],
        effect: { stat: "sectorDiversity", add: 1 },
        label: "+1 diversity — extraction nodes reduced further", isBig: true },
      { cost: [{ id: "neural_link", qty: 40 }, { id: "warp_coolant", qty: 55 }, { id: "enriched_cells", qty: 22 }],
        effect: { stat: "sectorDiversity", add: 1 },
        label: "+1 diversity — rare sites dominate the map", isBig: true },
    ],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // XP BRANCH
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: "xp_boost",
    name: "Neural Amplifier",
    desc: "Boost your ship's data processing core to extract more experience from every encounter.",
    tier: 1, req: [],
    badge: "XP",
    levels: buildLevels(10, {
      isBig,
      getEffect: lvl => ({ stat: "xpMult", mult: isBig(lvl) ? 0.40 : 0.10 }),
      getLabel:  lvl => isBig(lvl) ? `+40% XP gain` : `+10% XP gain`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "ionized_lens", qty: sc(10, 0.5, lvl) }];
        if (lvl <= 5)  return [{ id: "ionized_lens", qty: 75 }, { id: "fuel", qty: 30 }];
        if (lvl <= 9)  return [{ id: "ionized_lens", qty: sc(22, 0.42, lvl - 5) }, { id: "fuel", qty: sc(14, 0.42, lvl - 5) }, { id: "synth_alloy", qty: sc(10, 0.42, lvl - 5) }];
        return [{ id: "ionized_lens", qty: 160 }, { id: "fuel", qty: 110 }, { id: "warp_coolant", qty: 22 }];
      },
    }),
  },

  {
    id: "xp_miner",
    name: "Miner's Log",
    desc: "Earn bonus experience from each piece of debris destroyed.",
    tier: 2, req: [{ id: "xp_boost", minLevel: 1 }],
    badge: "XP",
    levels: buildLevels(8, {
      isBig,
      getEffect: lvl => ({ stat: "xpPerMine", add: isBig(lvl) ? 12 : 4 }),
      getLabel:  lvl => isBig(lvl) ? `+12 XP per debris` : `+4 XP per debris`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "synth_alloy", qty: sc(14, 0.5, lvl) }, { id: "ionized_lens", qty: sc(8, 0.45, lvl) }];
        if (lvl <= 5)  return [{ id: "synth_alloy", qty: 100 }, { id: "ionized_lens", qty: 60 }, { id: "fuel", qty: 30 }];
        if (lvl <= 7)  return [{ id: "synth_alloy", qty: sc(28, 0.42, lvl - 5) }, { id: "fuel", qty: sc(18, 0.42, lvl - 5) }, { id: "warp_coolant", qty: sc(6, 0.5, lvl - 5) }];
        return [{ id: "synth_alloy", qty: 220 }, { id: "warp_coolant", qty: 45 }, { id: "neural_link", qty: 20 }];
      },
    }),
  },

  {
    id: "xp_hunter",
    name: "Hunter's Mark",
    desc: "Earn greater experience from each combat kill.",
    tier: 2, req: [{ id: "xp_boost", minLevel: 1 }],
    badge: "XP",
    levels: buildLevels(8, {
      isBig,
      getEffect: lvl => ({ stat: "xpPerKill", add: isBig(lvl) ? 40 : 10 }),
      getLabel:  lvl => isBig(lvl) ? `+40 XP per kill` : `+10 XP per kill`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "metal_alloy", qty: sc(14, 0.5, lvl) }, { id: "fuel", qty: sc(8, 0.5, lvl) }];
        if (lvl <= 5)  return [{ id: "metal_alloy", qty: 105 }, { id: "fuel", qty: 60 }, { id: "warp_coolant", qty: 12 }];
        if (lvl <= 7)  return [{ id: "metal_alloy", qty: sc(28, 0.42, lvl - 5) }, { id: "warp_coolant", qty: sc(5, 0.5, lvl - 5) }, { id: "neural_link", qty: sc(3, 0.5, lvl - 5) }];
        return [{ id: "metal_alloy", qty: 210 }, { id: "warp_coolant", qty: 50 }, { id: "neural_link", qty: 28 }];
      },
    }),
  },

  {
    id: "xp_levelup",
    name: "Level-Up Surge",
    desc: "On every level-up, restore hull and top off fuel.",
    tier: 3, req: [{ id: "xp_miner", minLevel: 2 }, { id: "xp_hunter", minLevel: 2 }],
    badge: "XP",
    levels: [
      { cost: [{ id: "fuel", qty: 65 }, { id: "synth_alloy", qty: 45 }, { id: "ionized_lens", qty: 32 }],
        effect: { stat: "xpLevelUpHeal", add: 15 },
        label: "Level-up restores 15 HP", isBig: true },
      { cost: [{ id: "fuel", qty: 90 }, { id: "warp_coolant", qty: 18 }, { id: "ionized_lens", qty: 50 }],
        effect: { stat: "xpLevelUpFuel", add: 1 },
        label: "Level-up also restores +1 fuel", isBig: false },
      { cost: [{ id: "warp_coolant", qty: 35 }, { id: "fuel", qty: 130 }, { id: "synth_alloy", qty: 75 }],
        effect: { stat: "xpLevelUpHeal", add: 20 },
        label: "Level-up now restores 35 HP total", isBig: true },
      { cost: [{ id: "warp_coolant", qty: 55 }, { id: "neural_link", qty: 22 }, { id: "fuel", qty: 160 }],
        effect: { stat: "xpLevelUpFuel", add: 1 },
        label: "Level-up restores +2 fuel total", isBig: false },
      { cost: [{ id: "neural_link", qty: 42 }, { id: "warp_coolant", qty: 75 }, { id: "enriched_cells", qty: 24 }],
        effect: { stat: "xpLevelUpHeal", add: 25 },
        label: "Level-up restores 60 HP total", isBig: true },
      { cost: [{ id: "neural_link", qty: 60 }, { id: "enriched_cells", qty: 40 }, { id: "warp_coolant", qty: 95 }],
        effect: { stat: "xpLevelUpFuel", add: 2 },
        label: "Level-up restores +4 fuel total", isBig: true },
    ],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // TRADE BRANCH
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: "trade_access",
    name: "Trade Beacon",
    desc: "Establish a galaxy-wide trade network. Once active, trade is available from any node.",
    tier: 4, req: [{ id: "nav1", minLevel: 1 }],
    badge: "TRADE",
    minOutposts: 5,
    levels: [
      { cost: [{ id: "warp_coolant", qty: 40 }, { id: "fuel", qty: 100 }, { id: "ionized_lens", qty: 70 }, { id: "synth_alloy", qty: 60 }],
        effect: { unlock: "trade_anywhere" },
        label: "Trade market accessible from any node", isBig: true },
    ],
  },

  {
    id: "trade_refresh",
    name: "Market Uplink",
    desc: "Reduce the shop reset cooldown.",
    tier: 2, req: [{ id: "nav1", minLevel: 1 }],
    badge: "TRADE",
    levels: [
      { cost: [{ id: "fuel", qty: 45 }, { id: "synth_alloy", qty: 30 }],
        effect: { stat: "tradeCooldownHours", set: 20 },
        label: "Shop reset: 24h → 20h", isBig: false },
      { cost: [{ id: "fuel", qty: 80 }, { id: "ionized_lens", qty: 40 }, { id: "synth_alloy", qty: 50 }],
        effect: { stat: "tradeCooldownHours", set: 16 },
        label: "Shop reset: 20h → 16h", isBig: false },
      { cost: [{ id: "warp_coolant", qty: 22 }, { id: "fuel", qty: 120 }, { id: "optical_processor", qty: 55 }],
        effect: { stat: "tradeCooldownHours", set: 12 },
        label: "Shop reset: 16h → 12h", isBig: true },
      { cost: [{ id: "warp_coolant", qty: 48 }, { id: "neural_link", qty: 24 }, { id: "fuel", qty: 160 }],
        effect: { stat: "tradeCooldownHours", set: 6 },
        label: "Shop reset: 12h → 6h", isBig: false },
      { cost: [{ id: "neural_link", qty: 55 }, { id: "enriched_cells", qty: 32 }, { id: "warp_coolant", qty: 75 }],
        effect: { stat: "tradeCooldownHours", set: 0 },
        label: "No cooldown — instant shop refresh", isBig: true },
    ],
  },

  {
    id: "trade_discount",
    name: "Bulk Broker",
    desc: "Reduce all buy prices at trade outposts.",
    tier: 2, req: [{ id: "trade_access", minLevel: 1 }],
    badge: "TRADE",
    levels: buildLevels(8, {
      isBig,
      getEffect: lvl => ({ stat: "tradeBuyMult", add: isBig(lvl) ? -0.10 : -0.03 }),
      getLabel:  lvl => isBig(lvl) ? `−10% buy prices` : `−3% buy prices`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "ionized_lens", qty: sc(22, 0.5, lvl) }, { id: "synth_alloy", qty: sc(14, 0.5, lvl) }];
        if (lvl <= 5)  return [{ id: "ionized_lens", qty: 160 }, { id: "synth_alloy", qty: 100 }, { id: "fuel", qty: 55 }];
        if (lvl <= 7)  return [{ id: "ionized_lens", qty: sc(40, 0.42, lvl - 5) }, { id: "warp_coolant", qty: sc(12, 0.5, lvl - 5) }, { id: "fuel", qty: sc(22, 0.42, lvl - 5) }];
        return [{ id: "ionized_lens", qty: 300 }, { id: "warp_coolant", qty: 70 }, { id: "neural_link", qty: 30 }];
      },
    }),
  },

  {
    id: "trade_sell",
    name: "Fence Network",
    desc: "Command higher sell prices for your goods.",
    tier: 3, req: [{ id: "trade_discount", minLevel: 2 }],
    badge: "TRADE",
    levels: buildLevels(6, {
      isBig,
      getEffect: lvl => ({ stat: "tradeSellBonus", add: isBig(lvl) ? 0.08 : 0.03 }),
      getLabel:  lvl => isBig(lvl) ? `+8% sell ratio` : `+3% sell ratio`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "fuel", qty: sc(30, 0.5, lvl) }, { id: "ionized_lens", qty: sc(18, 0.5, lvl) }];
        if (lvl <= 5)  return [{ id: "fuel", qty: 220 }, { id: "ionized_lens", qty: 130 }, { id: "warp_coolant", qty: 28 }];
        return [{ id: "fuel", qty: 340 }, { id: "warp_coolant", qty: 65 }, { id: "neural_link", qty: 30 }];
      },
    }),
  },

  {
    id: "trade_stock",
    name: "Supply Chain",
    desc: "Increase stock quantities in the shop.",
    tier: 3, req: [{ id: "trade_refresh", minLevel: 2 }],
    minOutposts: 3,
    badge: "TRADE",
    levels: buildLevels(6, {
      isBig,
      getEffect: lvl => ({ stat: "tradeStockMult", mult: isBig(lvl) ? 0.50 : 0.15 }),
      getLabel:  lvl => isBig(lvl) ? `+50% shop stock` : `+15% shop stock`,
      getCost: lvl => {
        if (lvl <= 4)  return [{ id: "synth_alloy", qty: sc(22, 0.5, lvl) }, { id: "metal_alloy", qty: sc(14, 0.5, lvl) }];
        if (lvl <= 5)  return [{ id: "synth_alloy", qty: 160 }, { id: "metal_alloy", qty: 100 }, { id: "fuel", qty: 55 }];
        return [{ id: "synth_alloy", qty: 250 }, { id: "fuel", qty: 130 }, { id: "warp_coolant", qty: 35 }];
      },
    }),
  },

  {
    id: "trade_discovery",
    name: "Discovery Catalog",
    desc: "Unlock access to rare Discovery items in the shop.",
    tier: 4, req: [{ id: "trade_stock", minLevel: 2 }, { id: "trade_sell", minLevel: 2 }],
    badge: "TRADE",
    levels: [
      { cost: [{ id: "warp_coolant", qty: 30 }, { id: "ionized_lens", qty: 70 }, { id: "fuel", qty: 90 }],
        effect: { unlock: "discovery_raw" },
        label: "Unlocks Raw Discovery items", isBig: true },
      { cost: [{ id: "warp_coolant", qty: 65 }, { id: "neural_link", qty: 35 }, { id: "enriched_cells", qty: 22 }],
        effect: { unlock: "discovery_refined" },
        label: "Unlocks Refined Discovery items", isBig: true },
      { cost: [{ id: "neural_link", qty: 70 }, { id: "enriched_cells", qty: 50 }, { id: "quantum_core", qty: 1 }],
        effect: { unlock: "discovery_exotic" },
        label: "Unlocks Exotic Discovery items", isBig: true },
    ],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // COMBAT QTE
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: "ai_tracking",
    name: "AI Tracking",
    desc: "Widens the safe zone on Interceptor Escape Slider minigames.",
    tier: 3, req: [{ id: "blaster1", minLevel: 2 }],
    badge: "COMBAT QTE",
    levels: buildLevels(5, {
      isBig,
      getEffect: lvl => ({ stat: "qteSliderWidth", add: isBig(lvl) ? 0.08 : 0.03 }),
      getLabel:  lvl => isBig(lvl) ? `+8% slider green zone` : `+3% slider green zone`,
      getCost: lvl => [
        { id: "optical_processor", qty: sc(28, 0.5, lvl) },
        { id: "synth_alloy",       qty: sc(16, 0.5, lvl) },
      ],
    }),
  },

  {
    id: "firewall_patch",
    name: "Firewall Patch",
    desc: "Failed Frigate Sequence Intercepts deal less hull damage.",
    tier: 3, req: [{ id: "shield1", minLevel: 1 }],
    badge: "COMBAT QTE",
    levels: buildLevels(5, {
      isBig,
      getEffect: lvl => ({ stat: "qteFrigateMitigation", add: isBig(lvl) ? 0.20 : 0.08 }),
      getLabel:  lvl => isBig(lvl) ? `−20% failed intercept damage` : `−8% failed intercept damage`,
      getCost: lvl => [
        { id: "fuel",        qty: sc(32, 0.5, lvl) },
        { id: "synth_alloy", qty: sc(20, 0.5, lvl) },
      ],
    }),
  },

  {
    id: "orbital_drill",
    name: "Orbital Drill",
    desc: "The Boss Precision Breach ring rotates more slowly.",
    tier: 4, req: [{ id: "ai_tracking", minLevel: 1 }, { id: "firewall_patch", minLevel: 1 }],
    badge: "COMBAT QTE",
    levels: buildLevels(5, {
      isBig,
      getEffect: lvl => ({ stat: "qteBreachSlow", add: isBig(lvl) ? 0.15 : 0.05 }),
      getLabel:  lvl => isBig(lvl) ? `−15% breach ring speed` : `−5% breach ring speed`,
      getCost: lvl => [
        { id: "warp_coolant",  qty: sc(18, 0.55, lvl) },
        { id: "fuel",          qty: sc(40, 0.5, lvl) },
        { id: "enriched_cells",qty: sc(8, 0.6, lvl) },
      ],
    }),
  },

  // ──────────────────────────────────────────────────────────────────────────
  // ECHOES TREE
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "echoFrequency",
    name: "Echo Frequency",
    desc: "Echoes spawn more often from all gameplay actions.",
    tier: 1, req: [],
    badge: "ECHOES",
    levels: buildLevels(15, {
      isBig,
      getEffect: lvl => {
        const adds = [0.10,0.10,0.15,0.15,0.20,0.20,0.25,0.25,0.30,0.35,0.40,0.50,0.60,0.70,1.00];
        return { stat: "echoFrequency", add: adds[lvl - 1] };
      },
      getLabel: lvl => {
        const adds = ["10%","10%","15%","15%","20%","20%","25%","25%","30%","35%","40%","50%","60%","70%","100%"];
        return `Echoes spawn ${adds[lvl - 1]} more often`;
      },
      getCost: lvl => {
        if (lvl <= 2) return [{ id: "ionized_lens", qty: sc(50, 0.35, lvl) }, { id: "copper", qty: sc(40, 0.35, lvl) }];
        if (lvl <= 6) return [{ id: "optical_processor", qty: sc(8, 0.4, lvl - 2) }, { id: "ionized_lens", qty: sc(80, 0.35, lvl - 2) }];
        if (lvl <= 11) return [{ id: "nano_glass", qty: sc(8, 0.45, lvl - 6) }, { id: "fuel", qty: sc(60, 0.4, lvl - 6) }];
        return [{ id: "warp_coolant", qty: sc(12, 0.5, lvl - 11) }, { id: "enriched_cells", qty: sc(8, 0.55, lvl - 11) }];
      },
    }),
  },

  {
    id: "echoQuantity",
    name: "Echo Quantity",
    desc: "Each echo event rewards more Echo Currency.",
    tier: 1, req: [],
    badge: "ECHOES",
    levels: buildLevels(8, {
      isBig: lvl => new Set([1,4,6,8]).has(lvl),
      getEffect: () => ({ stat: "echoCurrencyBonus", add: 1 }),
      getLabel: () => "+1 Echo Currency per event",
      getCost: lvl => {
        if (lvl === 1) return [{ id: "ionized_lens", qty: 60 }, { id: "copper", qty: 50 }];
        if (lvl === 2) return [{ id: "optical_processor", qty: 8 }, { id: "ionized_lens", qty: 100 }];
        if (lvl === 3) return [{ id: "optical_processor", qty: 14 }, { id: "synth_alloy", qty: 10 }, { id: "ionized_lens", qty: 140 }];
        if (lvl === 4) return [{ id: "nano_glass", qty: 8 }, { id: "fuel", qty: 10 }, { id: "ionized_lens", qty: 150 }];
        if (lvl === 5) return [{ id: "nano_glass", qty: 14 }, { id: "fuel", qty: 16 }, { id: "warp_coolant", qty: 8 }];
        if (lvl === 6) return [{ id: "nano_glass", qty: 20 }, { id: "warp_coolant", qty: 14 }, { id: "enriched_cells", qty: 12 }];
        if (lvl === 7) return [{ id: "warp_coolant", qty: 16 }, { id: "neural_link", qty: 18 }, { id: "enriched_cells", qty: 14 }];
        return [{ id: "warp_coolant", qty: 28 }, { id: "enriched_cells", qty: 20 }]; // lv8 END
      },
    }),
  },

  {
    id: "echoCardBoost",
    name: "Rift Echo Boost",
    desc: "Amplifies all active Rift Echo bonuses.",
    tier: 1, req: [],
    badge: "ECHOES",
    levels: buildLevels(15, {
      isBig,
      getEffect: lvl => {
        const adds = [0.05,0.05,0.08,0.08,0.10,0.10,0.12,0.12,0.15,0.18,0.20,0.22,0.25,0.28,0.35];
        return { stat: "echoCardBoost", add: adds[lvl - 1] };
      },
      getLabel: lvl => {
        const adds = ["5%","5%","8%","8%","10%","10%","12%","12%","15%","18%","20%","22%","25%","28%","35%"];
        return `+${adds[lvl - 1]} to all active Echo Card bonuses`;
      },
      getCost: lvl => {
        if (lvl <= 2) return [{ id: "copper", qty: sc(60, 0.35, lvl) }, { id: "ionized_lens", qty: sc(50, 0.35, lvl) }];
        if (lvl <= 6) return [{ id: "optical_processor", qty: sc(6, 0.4, lvl - 2) }, { id: "synth_alloy", qty: sc(6, 0.4, lvl - 2) }];
        if (lvl <= 11) return [{ id: "nano_glass", qty: sc(8, 0.45, lvl - 6) }, { id: "fuel", qty: sc(50, 0.4, lvl - 6) }];
        return [{ id: "warp_coolant", qty: sc(10, 0.5, lvl - 11) }, { id: "enriched_cells", qty: sc(6, 0.55, lvl - 11) }];
      },
    }),
  },

  {
    id: "activeCardSlots",
    name: "Active Rift Echo Slots",
    desc: "Unlock additional slots to have more Rift Echoes active simultaneously.",
    tier: 2, req: [],
    badge: "RIFT ECHOES",
    levels: [
      { cost: [{ id: "optical_processor", qty: 12 }, { id: "ionized_lens", qty: 100 }],
        effect: { stat: "activeCardSlots", set: 2 }, label: "Unlock slot 2 (2 echoes active)", isBig: true },
      { cost: [{ id: "optical_processor", qty: 20 }, { id: "ionized_lens", qty: 140 }],
        effect: { stat: "activeCardSlots", set: 2 }, label: "Slot 2 gains +10% bonus", isBig: false },
      { cost: [{ id: "nano_glass", qty: 12 }, { id: "optical_processor", qty: 18 }, { id: "fuel", qty: 60 }],
        effect: { stat: "activeCardSlots", set: 3 }, label: "Unlock slot 3 (3 echoes active)", isBig: true },
      { cost: [{ id: "nano_glass", qty: 20 }, { id: "optical_processor", qty: 26 }, { id: "fuel", qty: 80 }],
        effect: { stat: "activeCardSlots", set: 3 }, label: "Slot 3 gains +10% bonus", isBig: false },
      { cost: [{ id: "nano_glass", qty: 20 }, { id: "fuel", qty: 80 }, { id: "enriched_cells", qty: 14 }],
        effect: { stat: "activeCardSlots", set: 4 }, label: "Unlock slot 4 (4 echoes active)", isBig: true },
      { cost: [{ id: "void_matter", qty: 16 }, { id: "neural_link", qty: 18 }, { id: "enriched_cells", qty: 14 }],
        effect: { stat: "activeCardSlots", set: 4 }, label: "Slot 4 gains +10% bonus", isBig: false },
      { cost: [{ id: "void_matter", qty: 22 }, { id: "neural_link", qty: 24 }, { id: "enriched_cells", qty: 18 }],
        effect: { stat: "activeCardSlots", set: 5 }, label: "Unlock slot 5 (5 echoes active)", isBig: false },
      { cost: [{ id: "void_matter", qty: 30 }, { id: "neural_link", qty: 32 }, { id: "enriched_cells", qty: 24 }],
        effect: { stat: "activeCardSlots", set: 5 }, label: "Slot 5 gains +10% bonus", isBig: false },
      { cost: [{ id: "void_matter", qty: 50 }, { id: "neural_link", qty: 45 }, { id: "enriched_cells", qty: 40 }],
        effect: { stat: "activeCardSlots", set: 6 }, label: "Unlock slot 6 (6 echoes active)", isBig: true },
    ],
  },

];
